import * as React from 'react';
import { useState, useContext, useEffect, useLayoutEffect } from 'react';
import { Loader } from '../../components/Loader';
import { Col, Row, Input } from 'reactstrap';
import { Accounts as DDAccounts } from './dotdigital/Accounts';
import { Accounts as SlicktextAccounts } from './slicktext/Accounts';
import { Accounts as AttentiveAccounts } from './attentive/Accounts';
import { Accounts as TreasureDataAccounts } from './treasure-data/Accounts';
import { Accounts as CommunityAccounts } from './community/Accounts';
import { Accounts as BrazeAccounts } from './braze/Accounts';
import matchSorter from 'match-sorter';
import { EnumerationsContext } from '../enumerations/EnumerationsContext';
import { LabelsContext } from '../labels/LabelsContext';
import { CountriesContext } from '../country-manager/CountriesContext';
import { logger } from '../../logging';
import { useFeature } from 'flagged';
import {
  clearAccountsFilterPreferences,
  persistAccountsSharedPrefs,
} from './accountsPreferences';
import { getDAMApp } from '../../services/firebase';
import { getAuth } from 'firebase/auth';
import { AbilityContext } from '../../auth/Can';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useObjectVal } from 'react-firebase-hooks/database';
import { ref } from '../../utils/firebase';

const R = require('ramda');
const pagesStyles = require('../pages.css');
const accountsStyles = require('./accounts.css');

export interface Credential {
  name: string;
  description: string;
  username: string;
  password: string;
}

const fuzzyTextFilterFn = (rows: any, id: any, filterValue: any) => {
  return matchSorter(rows, filterValue, {
    keys: [(row: any) => row.values[id]],
  });
};

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val: any) => !val;
export const fuzzyTextFilterFnExp = fuzzyTextFilterFn;

export const AccountsContainer = () => {
  const { enumerations, loadingEnumerations } = useContext(EnumerationsContext);
  const { labels, loadingLabels } = useContext(LabelsContext);
  const { countries, loadingCountries } = useContext(CountriesContext);
  const [system, setSystemType] = useState(() => {
    try {
      return localStorage.getItem('last_selected_system') || 'dotdigital';
    } catch (e) {
      return 'dotdigital';
    }
  });

  const handleSystemChange = (e: any) => {
    const val = e.target.value;
    setSystemType(val);
    try {
      localStorage.setItem('last_selected_system', val);
    } catch (err) {
      logger.error('Failed to save last_selected_system to localStorage', err);
    }
  };

  const ability = useContext(AbilityContext);
  const damApp = getDAMApp();
  const damAuth = getAuth(damApp);
  const [user, authLoading] = useAuthState(damAuth as any);
  const uid = user?.uid;
  const sharedPrefsRef = uid
    ? ref(`user_preferences/${uid}/accounts/shared`)
    : ref('user_preferences/__pending__/accounts/shared');
  const [sharedPrefs, sharedPrefsLoading] = useObjectVal(sharedPrefsRef as any);
  const [showActive, setShowActive] = useState(true);
  const [filterResetKey, setFilterResetKey] = useState(0);

  // Restore system + showActive from RTDB before debounced persist runs (stale state otherwise).
  useLayoutEffect(() => {
    if (!uid || sharedPrefsLoading) {
      return;
    }
    const s = sharedPrefs as Record<string, unknown> | null;
    if (s && typeof s.system === 'string') {
      setSystemType(s.system);
    }
    if (typeof s?.showActive === 'boolean') {
      setShowActive(s.showActive as boolean);
    }
  }, [uid, sharedPrefsLoading, sharedPrefs]);

  // Debounced write of shared prefs (mirrors per-table sync in useAccountsTablePrefsSync).
  useEffect(() => {
    if (!uid || sharedPrefsLoading) {
      return;
    }
    const handle = window.setTimeout(() => {
      persistAccountsSharedPrefs(uid, { system, showActive }).catch((err) =>
        logger.error(`persistAccountsSharedPrefs: ${err}`)
      );
    }, 400);
    return () => window.clearTimeout(handle);
  }, [uid, system, showActive, sharedPrefsLoading]);

  /** Clears user_preferences/{uid}/accounts, resets shared UI, remounts child Accounts via key. */
  const resetAccountsFilters = async () => {
    if (!uid) {
      return;
    }
    try {
      await clearAccountsFilterPreferences(uid);
    } catch {
      return;
    }
    setSystemType('dotdigital');
    setShowActive(true);
    setFilterResetKey((k) => k + 1);
  };

  if (
    authLoading ||
    !uid ||
    !enumerations.enumerations ||
    loadingEnumerations ||
    !labels ||
    loadingLabels ||
    !countries ||
    loadingCountries
  ) {
    return <Loader loading={true} />;
  }

  const enumerationValues = enumerations.enumerations as any;

  const areAttentiveAccountsEnabled = useFeature('attentive-accounts');
  const isAttentiveAdmin = ability.can('manage', 'attentive_admin');

  const areBrazeAccountsEnabled = useFeature('braze-accounts');
  const hasBrazeAccess = ability.can('read', 'braze');

  const areTreasureDataAccountsEnabled = useFeature('treasure-data-admin');
  const isTreasureDataAdmin = ability.can('manage', 'treasure_data_admin');

  const areCommunityAccountsEnabled = useFeature('community-account-admin');
  const isCommunityAdmin = ability.can('manage', 'community_admin');

  let systems = Object.values(enumerationValues.systems) || [];

  if (!isAttentiveAdmin || !areAttentiveAccountsEnabled) {
    systems = systems.filter((system: any) => system.value !== 'attentive');
  }

  if (!isTreasureDataAdmin || !areTreasureDataAccountsEnabled) {
    systems = systems.filter((system: any) => system.value !== 'treasure_data');
  }

  if (!isCommunityAdmin || !areCommunityAccountsEnabled) {
    systems = systems.filter((system: any) => system.value !== 'community');
  }

  if (!hasBrazeAccess || !areBrazeAccountsEnabled) {
    systems = systems.filter((system: any) => system.value !== 'braze');
  }

  const systemOptions = R.map((system: any) => {
    return (
      <option value={system.value} key={system.value}>
        {system.label}
      </option>
    );
  }, systems);

  return (
    <div className={pagesStyles.fullHeight}>
      {systems && (
        <div className={`${pagesStyles.container} ${pagesStyles.fullHeight}`}>
          <Row className={pagesStyles.headerRow}>
            <Col sm={{ size: 12 }} className={pagesStyles.noPadding}>
              <h3>Accounts</h3>
            </Col>
          </Row>
          <Row className={accountsStyles.formRow}>
            <Col sm="12">
              <Input
                type="select"
                name="system"
                id="system"
                value={system}
                onChange={handleSystemChange}
                className={accountsStyles.systemSelector}
              >
                {systemOptions}
              </Input>
            </Col>
          </Row>
          {/* key bumps after reset so each provider remounts with fresh table instance. */}
          <div className={pagesStyles.fullHeight}>
            {system === 'dotdigital' ? (
              <DDAccounts
                key={filterResetKey}
                enumerations={enumerations}
                showActive={showActive}
                setShowActive={setShowActive}
                labelsModel={labels}
                countriesModel={countries}
                onResetAccountsFilters={resetAccountsFilters}
              />
            ) : null}
            {system === 'slicktext' ? (
              <SlicktextAccounts
                key={filterResetKey}
                enumerations={enumerations}
                showActive={showActive}
                setShowActive={setShowActive}
                countriesModel={countries}
                onResetAccountsFilters={resetAccountsFilters}
              />
            ) : null}
            {system === 'attentive' ? (
              <AttentiveAccounts
                key={filterResetKey}
                enumerations={enumerations}
                showActive={showActive}
                setShowActive={setShowActive}
                countriesModel={countries}
                onResetAccountsFilters={resetAccountsFilters}
              />
            ) : null}
            {system === 'treasure_data' ? (
              <TreasureDataAccounts
                key={filterResetKey}
                showActive={showActive}
                setShowActive={setShowActive}
                onResetAccountsFilters={resetAccountsFilters}
              />
            ) : null}
            {system === 'community' ? (
              <CommunityAccounts
                key={filterResetKey}
                showActive={showActive}
                setShowActive={setShowActive}
                countriesModel={countries}
                onResetAccountsFilters={resetAccountsFilters}
              />
            ) : null}
            {system === 'braze' ? (
              <BrazeAccounts
                key={filterResetKey}
                enumerations={enumerations}
                showActive={showActive}
                setShowActive={setShowActive}
                countriesModel={countries}
                onResetAccountsFilters={resetAccountsFilters}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};
